import axios from 'axios';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import { exhaustMap, filter, map, switchMap } from 'rxjs/internal/operators';
import { IBusStop } from '../../interfaces/stadtbus/bus_stop'
import { lazyInject, provideSingleton } from '../../inversify';
import Configuration from '../configuration';
import LocalStorageService from '../local_storage_service';

@provideSingleton(BusStopService)
class BusStopService {
  // The default stop id that is used if no favorite stop has been selected.
  private static DEFAULT_STOP_ID: string = '2044';

  /**
   * Subscribe to receive a list of all bus stops.
   *
   * @type {BehaviorSubject<IBusStop[]>}
   */
  public readonly onBusStops: BehaviorSubject<IBusStop[]> = new BehaviorSubject<IBusStop[]>([]);

  /**
   * Subscribe to receive the favorite bus stop (or the default one if no favorite has been selected).
   *
   * @type {BehaviorSubject<IBusStop | undefined>}
   */
  public readonly onFavoriteBusStop: BehaviorSubject<IBusStop | undefined> = new BehaviorSubject<IBusStop | undefined>(undefined);

  /**
   * Emits a message if the bus stops need to be updated.
   */
  private readonly onUpdateBusStops: Subject<void> = new Subject<void>();

  @lazyInject(Configuration)
  private readonly configuration: Configuration;
  @lazyInject(LocalStorageService)
  private localStorageService: LocalStorageService;

  constructor() {
    this.localStorageService.getItem('busStops', [])
      .pipe(
        filter(stops => {
          // Check if the bus stop is valid
          if (stops && Array.isArray(stops) && stops.length > 0) {
            return true;
          }

          this.onUpdateBusStops.next();
          return false;
        }),
        map(s => s as IBusStop[]),
      )
      .subscribe(this.onBusStops);

    this.localStorageService.getItem('favoriteBusStop', BusStopService.DEFAULT_STOP_ID)
      .pipe(switchMap(stopId => this.onBusStopExists(stopId, BusStopService.DEFAULT_STOP_ID)))
      .subscribe(this.onFavoriteBusStop);

    this.onUpdateBusStops.pipe(
      exhaustMap(() =>
        fromPromise(axios
          .get(`${this.configuration.apiBaseUrl()}/stops`)
          .then(response => response.data as IBusStop[])
          .catch(() => undefined)
        )
      ),
    ).subscribe(stops => {
      if (stops !== undefined) {
        this.localStorageService.setItem('busStops', stops);
      }
    });

    // Update each hour
    const period = 60 * 60 * 1000;
    timer(0, period).subscribe(() => this.onUpdateBusStops.next());
  }

  /**
   * Update the favorite bus stop with a [stopId] and trigger an update [onFavoriteBusStop]. If no stopId is provided,
   * the function retrieves the favorite from the local storage or the default one, and emits it.
   *
   * @param {string} stopId
   */
  public updateFavoriteBusStopId(stopId: string) {
    this.localStorageService.setItem('favoriteBusStop', stopId);
  }

  /**
   * Subscribe to check if a stop with the [stopId] exists. The Observable returns a bus stop of undefined, if not exists.
   * If a [defaultStopId] is specified, this is returned instead.
   *
   * @param {string | undefined} stopId
   * @param {string} defaultStopId
   * @returns {Observable<IBusStop | undefined>}
   */
  public onBusStopExists(stopId: string | undefined, defaultStopId?: string): Observable<IBusStop | undefined> {
    if (stopId === undefined && defaultStopId === undefined) {
      return of(undefined);
    } else {
      return this.onBusStops.asObservable()
        .pipe(
          filter(stops => stops.length !== 0),
          map(stops => stops.find((s: IBusStop) => s.id === stopId)),
          switchMap(stop => stop === undefined && defaultStopId ? this.onBusStop(defaultStopId) : of(stop)),
        );
    }
  }

  /**
   * Receive a bus stop with [stopId]. If the id doesn't exist, no event is emitted.
   * @param {string} stopId
   * @returns {Observable<IBusStop>}
   */
  public onBusStop(stopId: string): Observable<IBusStop> {
    return this.onBusStopExists(stopId)
      .pipe(
        filter(s => s !== undefined),
        map(s => s as IBusStop),
      );
  }
}

export default BusStopService;



// WEBPACK FOOTER //
// ./src/api/stadtbus/bus_stop_service.ts